import type { ViewModel } from '@/screens/generated/view-model';
import type { Ctx } from './ctx';

/** The Trading-day drill-down — the reference's `dayModel()`.
 *
 *  **The P&L calendar owns the day's realised figure, and everything here is solved back from it.** That single rule
 *  is what makes the panel trustworthy: the trade rows sum to the calendar cell exactly, the R column agrees with the
 *  money column, and the gross win/loss pair reconciles with the win/loss counts. A drill-down that disagreed with the
 *  cell you clicked would be worse than no drill-down, because the reader has no way to tell which one is wrong.
 *
 *  The order of the arithmetic is the load-bearing part, and it is the reference's:
 *
 *  1. **R multiples come first.** A loser stops out at 1R — or less, if it was cut early — because a stop-out is the
 *     worst case by definition. A winner runs 0.8R–3R.
 *  2. **The risk unit is then solved** as net P&L ÷ net R. So the dollars and the R column cannot disagree, and the
 *     "risked per trade" figure is derived rather than asserted.
 *  3. **The winners' total is solved to put net R on the day's own side of zero**, so the sign is right by
 *     construction. When it will not resolve, the winners are dropped rather than a loss being deepened past its
 *     stop — inventing a 2R loss to balance a day would contradict the stop-discipline claim the same panel makes.
 *  4. **The last row absorbs the rounding**, so the rows sum to the cell to the cent.
 *
 *  Everything is seeded from the day key, so the same day always tells the same story (ground rule 3), and the whole
 *  panel is labelled simulated by the surface that renders it.
 */

/** Deterministic 0..1 stream from the day key. */
function daySeed(key: string): () => number {
  let s = 0;
  for (let i = 0; i < key.length; i++) s = (s * 31 + key.charCodeAt(i)) % 233280;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const SYMBOLS = ['XAUUSD', 'EURUSD', 'GBPJPY', 'US30', 'USDJPY', 'NAS100'] as const;
const STRATEGIES = ['London Open Reversal', 'Breakout Pullback', 'Trend Continuation', 'Liquidity Sweep'];
const PHASES = ['pre-entry', 'in-trade', 'post-trade'];
/** Dollars of risk per lot at a one-unit stop, per instrument — so a heavier instrument shows a smaller position for
 *  the same money risked. */
const PER_LOT: Record<string, number> = {
  XAUUSD: 900,
  EURUSD: 700,
  GBPJPY: 1100,
  US30: 1500,
  USDJPY: 650,
  NAS100: 1300,
};

const money = (v: number): string =>
  (v < 0 ? '−$' : '+$') +
  Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const plain = (v: number): string =>
  '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** The closed panel. Every figure empty rather than stale: a drawer sliding shut should not flash the last day's
 *  numbers on its way out. */
function closed(c: Ctx): Partial<ViewModel> {
  return {
    dayPanelPe: 'none',
    dayScrimOp: '0',
    dayPanelX: '24px',
    dayTrades: [],
    dayShots: [],
    dayTags: [],
    dayAiBullets: [],
    dayDate: '',
    dayPnl: '',
    dayPnlFg: 'var(--text)',
    dayCount: '',
    dayWr: '',
    dayMood: '',
    dayEnergy: '',
    dayPrep: '',
    dayPost: '',
    dayShotCount: '',
    dayGross: '',
    dayRisk: '',
    dayAiIdle: false,
    dayAiLoading: false,
    dayAiDone: false,
    dayAiVerdict: '',
    dayAiVerdictFg: 'var(--text)',
    dayAiAction: '',
    dayClose: () => c.set({ dayKey: null }),
    dayRunAi: () => {},
  };
}

export function buildDay(c: Ctx): Partial<ViewModel> {
  const S = c.S;
  if (!S.dayKey) return closed(c);

  const [year = 0, month = 1, dayOfMonth = 1] = S.dayKey.split('-').map(Number);
  const dt = new Date(Date.UTC(year, month - 1, dayOfMonth));
  const pnl = S.dayVal || 0;
  const r = daySeed(S.dayKey);

  // Trade count scales with the day's magnitude; the win/loss split is seeded, never asserted.
  const mag = Math.abs(pnl);
  const n = mag >= 3000 ? 4 : mag >= 1500 ? 3 : mag >= 600 ? 2 : 1;
  const up = pnl >= 0;
  let losses = n === 1 ? (up ? 0 : 1) : Math.max(1, Math.min(n - 1, Math.round(r() * (n - 1)) || 1));
  let wins = n - losses;
  if (up && wins === 0) {
    wins = 1;
    losses = n - 1;
  }
  if (!up && losses === 0) {
    losses = 1;
    wins = n - 1;
  }

  // Step 1–3: R multiples, then the risk unit. See the note at the top of this file.
  let rMults: number[] = [];
  let netR = 0;
  for (let guard = 0; guard < 6; guard++) {
    // Losers first, and never worse than 1R: a stop-out is the worst case by definition.
    const lossR = Array.from(
      { length: losses },
      () => -(r() < 0.72 ? 1 : Math.round((0.3 + r() * 0.5) * 100) / 100),
    );
    const lossTot = -lossR.reduce((a, b) => a + b, 0);
    if (!wins) {
      rMults = lossR;
      netR = -lossTot;
      break;
    }
    const winTot = up ? lossTot + (0.4 + r() * 1.6) : Math.max(0.2 * wins, lossTot - (0.3 + r() * 0.9));
    // Spread an implausibly large winner over more entries, or an implausibly small one over fewer, rather than
    // printing a single 5R trade or a rounding-error win.
    if (winTot / wins > 3.2 && wins < n) {
      wins++;
      losses--;
      continue;
    }
    if (winTot / wins < 0.2 && wins > 1) {
      wins--;
      losses++;
      continue;
    }
    const wt = Array.from({ length: wins }, () => 0.7 + r() * 0.6);
    const ws = wt.reduce((a, b) => a + b, 0);
    const winR = wt.map((x) => Math.round(((winTot * x) / ws) * 100) / 100);
    rMults = winR.concat(lossR);
    netR = rMults.reduce((a, b) => a + b, 0);
    if (up ? netR >= 0.2 : netR <= -0.2) break;
    // Drop the winners rather than blow a stop: deepening a loss past 1R to force the sign would contradict the
    // stop-discipline this same panel reports.
    if (!up && netR > -0.2) {
      rMults = lossR;
      netR = -lossTot;
      wins = 0;
      losses = rMults.length;
    }
    break;
  }

  const unit = pnl / netR; // dollars risked per trade, solved — not asserted
  for (let i = rMults.length - 1; i > 0; i--) {
    const j = Math.floor(r() * (i + 1));
    [rMults[i], rMults[j]] = [rMults[j]!, rMults[i]!];
  }
  const amounts = rMults.map((m) => Math.round(m * unit * 100) / 100);
  // Step 4: the last row absorbs the rounding, so the rows sum to the cell exactly.
  amounts[amounts.length - 1] =
    Math.round((pnl - amounts.slice(0, -1).reduce((a, b) => a + b, 0)) * 100) / 100;

  const grossWin = amounts.filter((v) => v > 0).reduce((a, b) => a + b, 0);
  const grossLoss = -amounts.filter((v) => v < 0).reduce((a, b) => a + b, 0);

  let mins = 7 * 60 + 20 + Math.floor(r() * 40);
  const rows = amounts.map((v, i) => {
    if (i) mins += 25 + Math.floor(r() * 130);
    const hh = Math.floor(mins / 60);
    const mm = mins % 60;
    const sess = hh < 12 ? 'London' : hh < 16 ? 'Overlap' : 'New York';
    const sym = SYMBOLS[Math.floor(r() * SYMBOLS.length)]!;
    // Size solves from the same risk unit through the instrument's own $/lot at stop, so a wider stop or a heavier
    // instrument shows a smaller position.
    const stopMult = 0.7 + r() * 0.7;
    const side = r() > 0.45 ? 'LONG' : 'SHORT';
    return {
      sym,
      side,
      lots: Math.max(0.01, Math.round((unit / (PER_LOT[sym]! * stopMult)) * 100) / 100).toFixed(2),
      time: String(hh).padStart(2, '0') + ':' + String(mm).padStart(2, '0'),
      sess,
      strat: STRATEGIES[Math.floor(r() * STRATEGIES.length)]!,
      pnl: v,
      pnlLabel: money(v),
      pnlFg: v >= 0 ? 'var(--pos)' : 'var(--neg)',
      r: (v >= 0 ? '+' : '−') + Math.abs(v / unit).toFixed(2) + 'R',
      sideFg: r() > 0.45 ? 'var(--pos)' : 'var(--neg)',
      bg:
        v >= 0
          ? 'color-mix(in srgb, var(--pnl-p4) 6%, transparent)'
          : 'color-mix(in srgb, var(--pnl-n4) 6%, transparent)',
    };
  });

  const wr = n ? (wins / n) * 100 : 0;
  const first = rows[0]!;
  const best = rows.reduce((a, b) => (b.pnl > a.pnl ? b : a), first);
  const worst = rows.reduce((a, b) => (b.pnl < a.pnl ? b : a), first);
  const sessCount = new Map<string, number>();
  for (const t of rows) sessCount.set(t.sess, (sessCount.get(t.sess) ?? 0) + 1);
  const topSess = [...sessCount.entries()].sort(([, a], [, b]) => b - a)[0]![0];
  const topSessCount = sessCount.get(topSess)!;
  const pf = grossLoss ? grossWin / grossLoss : Infinity;

  const moods = up ? ['Focused', 'Calm', 'Sharp'] : ['Flat', 'Frustrated', 'Tired'];
  const mood = moods[Math.floor(r() * moods.length)]!;
  const energy = up ? 6 + Math.floor(r() * 3) : 4 + Math.floor(r() * 3);
  const onPlan = up ? r() > 0.25 : r() > 0.6;

  const stratsRun = rows.map((t) => t.strat).filter((s, i, a) => a.indexOf(s) === i);
  const armedList =
    stratsRun.length === 1
      ? stratsRun[0]!
      : stratsRun.slice(0, -1).join(', ') + ' and ' + stratsRun[stratsRun.length - 1];
  const prep =
    armedList +
    (stratsRun.length === 1 ? ' armed on ' : ' armed, opening on ') +
    first.sym +
    ' at the ' +
    first.sess +
    ' open, ' +
    Math.max(2, n) +
    '-per-session cap, ' +
    (energy >= 7 ? 'full risk.' : 'half risk while energy is down.') +
    (up ? ' No entries outside the armed playbook.' : ' Stand down if the first entry fails.');
  const post =
    (up ? 'Closed the day ' : 'Gave back ') +
    plain(pnl) +
    ' across ' +
    (n === 1 ? '1 trade' : n + ' trades') +
    ' — ' +
    wins +
    (wins === 1 ? ' winner' : ' winners') +
    ', ' +
    losses +
    (losses === 1 ? ' loser' : ' losers') +
    '. ' +
    (wins
      ? 'Best rep was the ' +
        best.sym +
        ' ' +
        best.side.toLowerCase() +
        ' at ' +
        best.time +
        ' (' +
        best.r +
        ').'
      : 'Smallest loss was the ' +
        best.sym +
        ' ' +
        best.side.toLowerCase() +
        ' at ' +
        best.time +
        ' (' +
        best.r +
        ').') +
    (onPlan
      ? ' Every entry inside the armed playbook.'
      : ' The ' + worst.sym + ' entry at ' + worst.time + ' was off-plan — it broke the confirmation rule.');

  const tags = (onPlan ? ['Followed plan'] : ['Off-plan']).concat(
    up ? (wr >= 60 ? ['Clean entry'] : ['Patient']) : energy <= 5 ? ['Low energy'] : ['Cut early'],
  );

  const shots = rows.map((t, i) => ({
    sym: t.sym,
    time: t.time,
    phase: PHASES[i % 3]!,
    bd:
      t.pnl >= 0
        ? 'color-mix(in srgb, var(--pos) 26%, transparent)'
        : 'color-mix(in srgb, var(--neg) 26%, transparent)',
    bg:
      t.pnl >= 0
        ? 'color-mix(in srgb, var(--pnl-p4) 5%, transparent)'
        : 'color-mix(in srgb, var(--pnl-n4) 5%, transparent)',
  }));

  const st = S.dayAI || 'idle';
  const bullets = [
    {
      icon: 'ph-target',
      text:
        'Profit factor ' +
        (isFinite(pf) ? pf.toFixed(2) : '∞') +
        ' on the day — ' +
        plain(grossWin) +
        ' gross profit against ' +
        plain(grossLoss) +
        ' gross loss.',
    },
    {
      icon: 'ph-clock',
      text:
        topSessCount +
        ' of ' +
        n +
        ' ' +
        (n === 1 ? 'entry' : 'entries') +
        ' landed in the ' +
        topSess +
        ' window, ' +
        (topSessCount === n ? 'fully inside your declared session.' : 'the rest outside it.'),
    },
    {
      icon: up ? 'ph-trend-up' : 'ph-trend-down',
      text:
        'Largest ' +
        (up && wins ? 'winner' : 'loser') +
        ' was ' +
        (up && wins ? best.sym + ' ' + best.r : worst.sym + ' ' + worst.r) +
        ' — ' +
        Math.round(Math.abs((up ? best.pnl : worst.pnl) / (pnl || 1)) * 100) +
        '% of the day’s net.',
    },
    {
      icon: 'ph-notebook',
      text: onPlan
        ? 'Rule adherence clean: every entry matched the armed playbook.'
        : 'One entry breached the confirmation rule — the behavioural cost, not the dollar cost, is the finding here.',
    },
  ];

  return {
    dayPanelPe: 'auto',
    dayScrimOp: '1',
    dayPanelX: '0px',
    dayDate: DAYS[dt.getUTCDay()] + ', ' + MONTHS[month - 1] + ' ' + dayOfMonth + ', ' + year,
    dayPnl: money(pnl),
    dayPnlFg: pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
    dayCount: n === 1 ? '1 trade' : n + ' trades',
    dayWr: wr.toFixed(0) + '% win rate',
    dayGross: plain(grossWin) + ' / ' + plain(grossLoss),
    dayRisk: plain(unit) + ' risked per trade',
    dayMood: mood,
    dayEnergy: 'Energy ' + energy + '/10',
    dayPrep: prep,
    dayPost: post,
    dayTags: tags,
    dayTrades: rows,
    dayShots: shots,
    dayShotCount: shots.length + (shots.length === 1 ? ' screenshot' : ' screenshots'),
    dayAiIdle: st === 'idle',
    dayAiLoading: st === 'loading',
    dayAiDone: st === 'done',
    dayAiBullets: bullets,
    dayAiVerdict: up
      ? onPlan
        ? 'A clean, in-plan green day.'
        : 'Green on the P&L, loose on the process.'
      : onPlan
        ? 'A disciplined red day — the process held.'
        : 'Red day compounded by an off-plan entry.',
    dayAiVerdictFg: up && onPlan ? 'var(--pos)' : !up && !onPlan ? 'var(--neg)' : 'var(--amb-2)',
    dayAiAction: onPlan
      ? 'Keep the ' + topSess + ' window as your primary and leave the cap where it is.'
      : 'Add a hard confirmation check before any entry outside ' + topSess + '.',
    dayClose: () => c.set({ dayKey: null }),
    dayRunAi: () => {
      // Guarded: a second press while it is thinking would restart the timer and make the verdict take longer the
      // more impatient you are.
      if (c.S.dayAI === 'loading') return;
      c.set({ dayAI: 'loading' });
      c.timers.after(900, () => c.set({ dayAI: 'done' }), 'dayAi');
    },
  };
}
